/**
 * One contract for how this game's art is drawn, and which way it points.
 *
 * ## The convention, measured rather than assumed
 *
 * Every ship and cannon master in `assets-source/` is drawn pointing UP: the
 * silhouette's narrow end - a nose, a muzzle - sits at the top of the canvas,
 * along the texture's own `-Y`. Measured on the three cannon masters at the
 * time this was written:
 *
 * ```
 * cannon.png   opaque box 272x473  narrow end at the TOP  vertical asymmetry 60%
 * cannon2.png  opaque box 166x421  narrow end at the TOP  vertical asymmetry 29%
 * cannon3.png  opaque box 258x487  narrow end at the TOP  vertical asymmetry 62%
 * ```
 *
 * Horizontal asymmetry over the same bands: 0%, 3%, 3%. The art is not
 * ambiguous about which way it faces.
 *
 * ## Why a quarter turn
 *
 * The simulation's angles are WORLD angles: zero points along `+X` and they
 * grow towards `+Y`, which is what `Math.atan2(y, x)` returns and what
 * `slotToWorld` consumes. A Phaser sprite with rotation `r` maps its local
 * `(0, -1)` - the direction the art points - to world `(sin r, -cos r)`. Set
 * that equal to `(cos θ, sin θ)` and there is exactly one answer:
 *
 * ```
 * sin r = cos θ  and  -cos r = sin θ   =>   r = θ + π/2
 * ```
 *
 * ## The defect this exists to end (gate V27A)
 *
 * The hunt already applied that quarter turn to hulls and cannons alike. A
 * duel applied it to the hull - `pvpHullRotation` is `heading + π/2` - and
 * then rotated the BARRELS by the raw world angle, so every cannon in every
 * duel was drawn ninety degrees off, swivelling sideways while the shots left
 * along the aim. The player's word for it was "canhão de lado", and the
 * arithmetic agrees with them exactly.
 *
 * ## What this is NOT
 *
 * It is presentation, and only presentation. Nothing here places a hardpoint,
 * decides a muzzle, or is read by anything authoritative: `slotToWorld` takes
 * the WORLD angle, unmodified, and keeps taking it. Turning the sprite that
 * sits at a mount cannot move the mount.
 */

/**
 * The quarter turn between a world angle and the rotation of art drawn facing
 * up.
 *
 * A constant rather than a literal at four call sites, because four literals
 * are four opinions and one of them was already wrong.
 */
export const ART_FACES_UP_OFFSET = Math.PI / 2;

/**
 * The sprite rotation that makes art drawn facing up point along `angle`.
 *
 * `angle` is a world angle - zero along `+X`, growing towards `+Y` - which is
 * what `Math.atan2` produces and what the simulation speaks.
 */
export function spriteRotationForWorldAngle(angle: number): number {
  return angle + ART_FACES_UP_OFFSET;
}

/**
 * Which way a sprite at rotation `r` is actually DRAWN pointing, as a unit
 * vector in world space.
 *
 * The inverse of the line above, written out so a test can assert the round
 * trip instead of restating the formula. This is the thing a player sees: the
 * texture's `-Y`, turned by the rotation the scene set.
 */
export function drawnForwardVector(rotation: number): { readonly x: number; readonly y: number } {
  return { x: Math.sin(rotation), y: -Math.cos(rotation) };
}
